import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Separator } from '../components/ui/separator';
import { 
  Send, Settings, FileText, History, CheckCircle2, XCircle, Clock,
  RefreshCw, Shield, ExternalLink, AlertTriangle, Download, Eye,
  Building2, Key, Server, Wifi, WifiOff, Lock, Info
} from 'lucide-react';

const RTIDashboardPage = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const isDirector = user?.role === 'director';
  const isFinance = ['director', 'finance'].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, configRes, submissionsRes] = await Promise.all([
        api.get('/rti/dashboard'),
        api.get('/rti/config'),
        api.get('/rti/submissions?limit=20')
      ]);
      setDashboard(dashboardRes.data);
      setConfig(configRes.data);
      setSubmissions(submissionsRes.data?.submissions || []);
    } catch (err) {
      console.error('Error fetching RTI data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setTestResult(null);
    try {
      const res = await api.post('/rti/config/test');
      setTestResult(res.data);
      fetchData(); // Refresh to get updated test status
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Connection test failed' });
    } finally {
      setTestingConnection(false);
    }
  };

  const formatCurrency = (amount) => `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const getStatusBadge = (status) => {
    const statusConfig = {
      'accepted': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'rejected': { color: 'bg-red-100 text-red-800', icon: XCircle },
      'error': { color: 'bg-red-100 text-red-800', icon: XCircle },
      'submitted': { color: 'bg-blue-100 text-blue-800', icon: Send },
      'validated': { color: 'bg-amber-100 text-amber-800', icon: Clock },
      'draft': { color: 'bg-gray-100 text-gray-800', icon: FileText }
    };
    const cfg = statusConfig[status] || statusConfig.draft;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rti-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">HMRC RTI Submissions</h1>
          <p className="text-slate-500">Real Time Information - Direct Government Gateway Integration</p>
        </div>
        <div className="flex items-center gap-2">
          {config?.is_configured ? (
            <Badge className="bg-green-100 text-green-800 px-3 py-1">
              <Wifi className="w-4 h-4 mr-1" />
              {config.environment === 'live' ? 'Live' : 'Test'} Environment
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-800 px-3 py-1">
              <WifiOff className="w-4 h-4 mr-1" />
              Not Configured
            </Badge>
          )}
        </div>
      </div>

      {/* Important Notice */}
      <Alert className="bg-blue-50 border-blue-200">
        <Info className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>HMRC Registration Required:</strong> Direct RTI submission to HMRC requires completing the 
          <a href="https://www.gov.uk/government/collections/real-time-information-online-internet-submissions-support-for-software-developers" 
             target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-1">
            HMRC Software Developer Registration
            <ExternalLink className="w-3 h-3 inline ml-1" />
          </a>. 
          Currently operating in <strong>simulation mode</strong> for testing.
        </AlertDescription>
      </Alert>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Submission History</TabsTrigger>
          {isDirector && (
            <TabsTrigger value="config" data-testid="tab-config">Configuration</TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-[#1e3a5f] to-[#2a5a8f] text-white">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/70 text-sm">Total Submissions</p>
                    <p className="text-3xl font-bold">{dashboard?.statistics?.total_submissions || 0}</p>
                  </div>
                  <Send className="w-10 h-10 text-white/30" />
                </div>
                <p className="text-white/60 text-xs mt-2">Tax Year {dashboard?.tax_year}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Accepted</p>
                    <p className="text-3xl font-bold text-green-600">{dashboard?.statistics?.accepted || 0}</p>
                  </div>
                  <CheckCircle2 className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-amber-600">{dashboard?.statistics?.pending || 0}</p>
                  </div>
                  <Clock className="w-10 h-10 text-amber-200" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Errors</p>
                    <p className="text-3xl font-bold text-red-600">{dashboard?.statistics?.errors || 0}</p>
                  </div>
                  <XCircle className="w-10 h-10 text-red-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Configuration Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5" />
                HMRC Connection Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${config?.is_configured ? 'bg-green-100' : 'bg-amber-100'}`}>
                    {config?.is_configured ? <CheckCircle2 className="w-6 h-6 text-green-600" /> : <AlertTriangle className="w-6 h-6 text-amber-600" />}
                  </div>
                  <div>
                    <p className="font-medium">Gateway Credentials</p>
                    <p className="text-sm text-slate-500">{config?.is_configured ? 'Configured' : 'Not configured'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${config?.environment === 'live' ? 'bg-green-100' : 'bg-blue-100'}`}>
                    <Shield className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium">Environment</p>
                    <p className="text-sm text-slate-500">{config?.environment === 'live' ? 'Live (Production)' : 'Test (Sandbox)'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${config?.last_test_result === 'success' ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Wifi className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium">Last Connection Test</p>
                    <p className="text-sm text-slate-500">
                      {config?.last_test_result ? 
                        `${config.last_test_result === 'success' ? 'Successful' : 'Failed'}` : 
                        'Never tested'}
                    </p>
                  </div>
                </div>
              </div>

              {isFinance && (
                <div className="mt-6 flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleTestConnection}
                    disabled={!config?.is_configured || testingConnection}
                  >
                    {testingConnection ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                    ) : (
                      <><Wifi className="w-4 h-4 mr-2" /> Test Connection</>
                    )}
                  </Button>
                  {isDirector && (
                    <Button onClick={() => setShowConfigDialog(true)}>
                      <Settings className="w-4 h-4 mr-2" />
                      Configure HMRC Settings
                    </Button>
                  )}
                </div>
              )}

              {testResult && (
                <Alert className={`mt-4 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <AlertDescription className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                    {testResult.success ? <CheckCircle2 className="w-4 h-4 inline mr-2" /> : <XCircle className="w-4 h-4 inline mr-2" />}
                    {testResult.message}
                    {testResult.note && <p className="mt-1 text-sm opacity-75">{testResult.note}</p>}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Recent Submissions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Recent Submissions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.recent_submissions?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent_submissions.map((sub, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1e3a5f] rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium">{sub.type} - Period {sub.period}</p>
                          <p className="text-sm text-slate-500">{sub.hmrc_ref || 'Pending HMRC Reference'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(sub.status)}
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedSubmission(sub); setShowSubmissionDialog(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Send className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No RTI submissions yet</p>
                  <p className="text-sm mt-1">Submissions will appear here after running payroll</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* How RTI Works */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Info className="w-5 h-5" />
                How RTI Submission Works
              </CardTitle>
            </CardHeader>
            <CardContent className="text-blue-800">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 shadow-sm">
                    <span className="text-xl font-bold text-blue-600">1</span>
                  </div>
                  <p className="font-medium">Complete Payroll</p>
                  <p className="text-sm text-blue-600">Run and approve your monthly/weekly payroll</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 shadow-sm">
                    <span className="text-xl font-bold text-blue-600">2</span>
                  </div>
                  <p className="font-medium">Prepare FPS</p>
                  <p className="text-sm text-blue-600">System generates XML in HMRC format</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 shadow-sm">
                    <span className="text-xl font-bold text-blue-600">3</span>
                  </div>
                  <p className="font-medium">Submit to HMRC</p>
                  <p className="text-sm text-blue-600">One-click submission via Government Gateway</p>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-2 shadow-sm">
                    <span className="text-xl font-bold text-blue-600">4</span>
                  </div>
                  <p className="font-medium">Get Confirmation</p>
                  <p className="text-sm text-blue-600">Receive HMRC reference and store in history</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submission History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>RTI Submission History</CardTitle>
              <CardDescription>All FPS, EPS and other RTI submissions to HMRC</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Tax Year</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Payment Date</TableHead>
                    <TableHead className="text-right">Employees</TableHead>
                    <TableHead className="text-right">Total Value</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>HMRC Ref</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <Badge variant="outline">{sub.submission_type}</Badge>
                      </TableCell>
                      <TableCell>{sub.tax_year}</TableCell>
                      <TableCell>{sub.period}</TableCell>
                      <TableCell>{sub.payment_date}</TableCell>
                      <TableCell className="text-right">{sub.employee_count || '-'}</TableCell>
                      <TableCell className="text-right">{formatCurrency(sub.total_value)}</TableCell>
                      <TableCell>{getStatusBadge(sub.status)}</TableCell>
                      <TableCell className="font-mono text-sm">{sub.hmrc_ref || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : '-'}
                        {sub.submitted_by_name && <span className="text-slate-400 block text-xs">{sub.submitted_by_name}</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedSubmission(sub); setShowSubmissionDialog(true); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {submissions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        No RTI submissions found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab (Director Only) */}
        {isDirector && (
          <TabsContent value="config" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Government Gateway Configuration
                </CardTitle>
                <CardDescription>
                  Configure your HMRC credentials for direct RTI submissions. These credentials are encrypted and stored securely.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert className="mb-6 bg-amber-50 border-amber-200">
                  <Lock className="w-4 h-4 text-amber-600" />
                  <AlertDescription className="text-amber-800">
                    <strong>Security Notice:</strong> Government Gateway credentials are encrypted at rest. 
                    Only Directors can view and modify these settings.
                  </AlertDescription>
                </Alert>

                {config?.is_configured ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-500">Employer PAYE Reference</Label>
                        <p className="font-medium">{config.employer_paye_ref || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Accounts Office Reference</Label>
                        <p className="font-medium">{config.accounts_office_ref || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Tax Office Number</Label>
                        <p className="font-medium">{config.tax_office_number || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Tax Office Reference</Label>
                        <p className="font-medium">{config.tax_office_ref || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Gateway User ID</Label>
                        <p className="font-medium font-mono">{config.gateway_user_id_masked || 'Not set'}</p>
                      </div>
                      <div>
                        <Label className="text-slate-500">Gateway Password</Label>
                        <p className="font-medium font-mono">{config.gateway_password_masked || 'Not set'}</p>
                      </div>
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Last updated: {config.updated_at ? new Date(config.updated_at).toLocaleString() : 'Never'}</p>
                        {config.updated_by_name && <p className="text-sm text-slate-500">By: {config.updated_by_name}</p>}
                      </div>
                      <Button onClick={() => setShowConfigDialog(true)}>
                        <Settings className="w-4 h-4 mr-2" />
                        Update Configuration
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-lg font-medium mb-2">HMRC Not Configured</h3>
                    <p className="text-slate-500 mb-4">Set up your Government Gateway credentials to enable direct RTI submissions</p>
                    <Button onClick={() => setShowConfigDialog(true)}>
                      <Settings className="w-4 h-4 mr-2" />
                      Configure Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* External Links */}
            <Card>
              <CardHeader>
                <CardTitle>HMRC Resources</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a href="https://www.gov.uk/government/collections/real-time-information-online-internet-submissions-support-for-software-developers" 
                     target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors">
                    <ExternalLink className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium">RTI Developer Guide</p>
                      <p className="text-sm text-slate-500">Official HMRC documentation for RTI</p>
                    </div>
                  </a>
                  <a href="https://www.gov.uk/paye-online" 
                     target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors">
                    <ExternalLink className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium">HMRC PAYE Online</p>
                      <p className="text-sm text-slate-500">Access your PAYE account online</p>
                    </div>
                  </a>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Configuration Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Configure HMRC Government Gateway</DialogTitle>
            <DialogDescription>
              Enter your HMRC credentials for direct RTI submission. These are encrypted and stored securely.
            </DialogDescription>
          </DialogHeader>
          <HMRCConfigForm api={api} existingConfig={config} onSuccess={() => { setShowConfigDialog(false); fetchData(); }} onClose={() => setShowConfigDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Submission Details Dialog */}
      <Dialog open={showSubmissionDialog} onOpenChange={setShowSubmissionDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Submission Details</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Submission Type</Label>
                  <p className="font-medium">{selectedSubmission.submission_type}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedSubmission.status)}</div>
                </div>
                <div>
                  <Label className="text-slate-500">Tax Year</Label>
                  <p className="font-medium">{selectedSubmission.tax_year}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Period</Label>
                  <p className="font-medium">{selectedSubmission.period}</p>
                </div>
                <div>
                  <Label className="text-slate-500">HMRC Reference</Label>
                  <p className="font-medium font-mono">{selectedSubmission.hmrc_ref || 'Pending'}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Submitted By</Label>
                  <p className="font-medium">{selectedSubmission.submitted_by_name || '-'}</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSubmissionDialog(false)}>Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// HMRC Configuration Form
const HMRCConfigForm = ({ api, existingConfig, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    employer_paye_ref: existingConfig?.employer_paye_ref || '',
    accounts_office_ref: existingConfig?.accounts_office_ref || '',
    tax_office_number: existingConfig?.tax_office_number || '',
    tax_office_ref: existingConfig?.tax_office_ref || '',
    employer_name: existingConfig?.employer_name || 'Innovative Care and Rehab Centre Limited',
    gateway_user_id: '',
    gateway_password: '',
    sender_id: existingConfig?.sender_id || '',
    environment: existingConfig?.environment || 'test'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!formData.employer_paye_ref || !formData.gateway_user_id || !formData.gateway_password) {
      alert('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      await api.put('/rti/config', formData);
      onSuccess();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Employer PAYE Reference *</Label>
          <Input 
            placeholder="123/AB12345"
            value={formData.employer_paye_ref}
            onChange={(e) => setFormData({...formData, employer_paye_ref: e.target.value})}
          />
        </div>
        <div>
          <Label>Accounts Office Reference *</Label>
          <Input 
            placeholder="123PB00012345"
            value={formData.accounts_office_ref}
            onChange={(e) => setFormData({...formData, accounts_office_ref: e.target.value})}
          />
        </div>
        <div>
          <Label>Tax Office Number</Label>
          <Input 
            placeholder="123"
            value={formData.tax_office_number}
            onChange={(e) => setFormData({...formData, tax_office_number: e.target.value})}
          />
        </div>
        <div>
          <Label>Tax Office Reference</Label>
          <Input 
            placeholder="AB12345"
            value={formData.tax_office_ref}
            onChange={(e) => setFormData({...formData, tax_office_ref: e.target.value})}
          />
        </div>
      </div>

      <div>
        <Label>Employer Name</Label>
        <Input 
          value={formData.employer_name}
          onChange={(e) => setFormData({...formData, employer_name: e.target.value})}
        />
      </div>

      <Separator />

      <div className="space-y-4">
        <h4 className="font-medium flex items-center gap-2">
          <Lock className="w-4 h-4" />
          Government Gateway Credentials
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Gateway User ID *</Label>
            <Input 
              placeholder="Enter User ID"
              value={formData.gateway_user_id}
              onChange={(e) => setFormData({...formData, gateway_user_id: e.target.value})}
            />
          </div>
          <div>
            <Label>Gateway Password *</Label>
            <Input 
              type="password"
              placeholder="Enter Password"
              value={formData.gateway_password}
              onChange={(e) => setFormData({...formData, gateway_password: e.target.value})}
            />
          </div>
        </div>
      </div>

      <div>
        <Label>Sender ID (HMRC-assigned)</Label>
        <Input 
          placeholder="Assigned after HMRC registration"
          value={formData.sender_id}
          onChange={(e) => setFormData({...formData, sender_id: e.target.value})}
        />
      </div>

      <div>
        <Label>Environment</Label>
        <Select value={formData.environment} onValueChange={(v) => setFormData({...formData, environment: v})}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="test">Test (Sandbox)</SelectItem>
            <SelectItem value="live">Live (Production)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500 mt-1">Use Test environment until HMRC registration is complete</p>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving} className="bg-[#1e3a5f]">
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Configuration'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default RTIDashboardPage;
